import Sugar from 'sugar';
import * as Yup from 'yup';

export const prettyName = (value?: string) => (value ? Sugar.String.titleize(value).replace(/\bId\b/g, 'ID') : '');

export const isValidEmail = (email: string): boolean => {
  const schema = Yup.object().shape({ email: Yup.string().email().required() });
  try {
    schema.validateSync({ email });
  } catch {
    return false;
  }
  return true;
};

export const getKeysOfMissingValues = (parameters: { [key: string]: unknown }): string[] => {
  return Object.entries(parameters)
    .map(([key, val]) => (val != null ? undefined : key))
    .compact();
};

/**
 * True when `value` is not null/undefined and has at least one non-whitespace character (after trim).
 * Narrows to `string` when true.
 *
 * Use this instead of lodash `isEmpty` for optional strings: `isEmpty('  ')` is false (non-zero length),
 * while here whitespace-only counts as empty. Lodash `isEmpty` also targets collections/objects with
 * different rules and is easy to misuse for string-or-null fields.
 *
 * The false branch is typed as null/undefined. A whitespace-only string is still a string at runtime,
 * so do not read that string from the false branch.
 */
export function isNonEmpty(value: string | null | undefined): value is string {
  return value != null && value.trim() !== '';
}

/**
 * Compact multiple spaces in `input` into a single space, and trim whitespace from the ends of the string.
 *
 * @param input Input string
 * @returns Compacted string
 */
export function compactString(input: string): string {
  return input.replaceAll(/[ ]+/g, ' ').trim();
}

/**
 * Parses a comma-separated string into an array of trimmed, non-empty strings.
 *
 * @param value - The comma-separated string to parse
 * @returns Array of trimmed strings, with empty strings filtered out
 *
 * @example
 * parseCommaSeparatedList('apple, banana, , cherry')
 * // Returns: ['apple', 'banana', 'cherry']
 */
export const parseCommaSeparatedList = (value: string | null | undefined): string[] => {
  if (!value || value.trim() === '') {
    return [];
  }

  return value
    .split(',')
    .map(item => item.trim())
    .filter(item => item.length > 0);
};

const ZERO_WIDTH_SPACE = '\u200B';

/** Inserts zero-width spaces after `,`, `:`, and `;` so long unbroken strings wrap at punctuation instead of mid-word. */
export function withSoftBreaks(value: string): string {
  return value.replace(/([,:;])(?=\S)/g, `$1${ZERO_WIDTH_SPACE}`);
}

/** Useful for parsing org configs into a Record (simple json object). Wraps the parsing with a try-catch and some basic checks. Returns undefined if it fails. */
export function parseJSONObject<Key extends string | number = string, Value = any>(
  value: string | null | undefined
): Record<Key, Value> | undefined {
  if (value == null || value === '') {
    return undefined;
  }

  try {
    return JSON.parse(value);
  } catch (e) {
    return undefined;
  }
}
